import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Upload, Shirt, Eye, ShoppingCart, Sparkles, Truck, Zap, Award, MapPin } from "lucide-react";
import { useEffect, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Marquee from "../components/Marquee";
import ReadyMadeCard from "../components/ReadyMadeCard";
import ReviewsCarousel from "../components/ReviewsCarousel";
import ExplainerVideo from "../components/ExplainerVideo";
import TrustBadges from "../components/TrustBadges";
import SEO from "../components/SEO";
import { listProducts, listReadyMade } from "../lib/api";
import { trackEvent } from "../lib/analytics";

const HERO_BG = "/img/hero@2x.webp";
const RACK = "https://static.prod-images.emergentagent.com/jobs/3334849c-f2a2-4fb8-ac40-f7b64f5a1060/images/a2e625975e7cc32adceb2500f069c561289e9d6f05fc4eaef41cfc866030fd01.png";
const SKYLINE = "https://images.unsplash.com/photo-1610509659326-b35b9b15bf51?w=1600&q=80";
const PRINT_PROCESS = "https://images.unsplash.com/photo-1663433567177-9f94be0bff4c?w=1200&q=80";
const MODEL = "https://images.unsplash.com/photo-1564557287817-3785e38ec1f5?w=1200&q=80";

// TESTIMONIALS moved to <ReviewsCarousel /> component (uses real GBP reviews
// with AggregateRating schema for rich-result eligibility).

const FAQ_PREVIEW = [
  { q: "How fast can you turn around a custom order?", a: "Standard turnaround is 5–7 business days. Rush orders (48-hour) available at checkout." },
  { q: "Is there a minimum order?", a: "No minimums on custom orders — order 1 piece or 1,000. Bulk discounts kick in at 25+ units." },
  { q: "Can I pick up locally?", a: "Yes — free local pickup at our Mississauga studio (2404 Haines Rd Suite 209)." },
  { q: "What file formats do you accept?", a: "PNG (with transparency), JPG, and SVG. High-res (300dpi+) is recommended for best print." },
];

export default function Landing() {
  const [customProducts, setCustomProducts] = useState([]);
  const [readyMade, setReadyMade] = useState([]);

  // iter78/78c — surface featured / best-selling products first. Tiers:
  //   TIER 1: featured==true OR badges includes "bestseller"/"new-drop"  (manually promoted)
  //   TIER 2: bestseller_rank<=10                                        (auto from sales)
  //   TIER 3: everything else, sorted by featured_order ASC
  const rankScore = (p) => {
    const badges = p.badges || [];
    if (p.featured || badges.includes("bestseller") || badges.includes("new-drop")) {
      return -20000 + (p.featured_order ?? 9999);
    }
    if (p.bestseller_rank && p.bestseller_rank <= 10) return -10000 + p.bestseller_rank;
    return (p.featured_order ?? 9999);
  };

  useEffect(() => {
    listProducts().then((arr) => {
      arr.sort((a, b) => rankScore(a) - rankScore(b));
      setCustomProducts(arr);
    }).catch(() => {});
    listReadyMade().then((arr) => {
      arr.sort((a, b) => rankScore(a) - rankScore(b));
      setReadyMade(arr);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-black">
      {/* Hero image preload — Landing-only so it doesn't trigger the
          "preloaded but not used" warning on other routes (iter47o). */}
      <Helmet>
        <link rel="preload" as="image" href="/img/hero@2x.webp" fetchPriority="high" />
      </Helmet>
      <SEO
        title="Custom T-Shirts Toronto · DTF & Embroidery · GTA"
        description="Custom t-shirts, hoodies & embroidery in Toronto & the GTA. Design online with our live designer. Bulk pricing, fast turnaround."
        keywords="custom t-shirts Toronto, DTF printing Toronto, embroidery Toronto, custom hoodies Mississauga, bulk t-shirt printing GTA, team uniforms Toronto"
        path="/"
        schema={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://torontocustomtshirts.ca/#business",
          "name": "Toronto Custom T-Shirts",
          "image": "https://torontocustomtshirts.ca/logo.png",
        }}
      />
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#222]" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Custom printed t-shirts and hoodies by Toronto Custom T-Shirts" fetchPriority="high" decoding="async" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />
        </div>
        <div className="relative max-w-[1500px] mx-auto px-6 lg:px-10 pt-16 pb-24 lg:pt-28 lg:pb-40 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-px w-12 bg-[var(--tc-red)]" />
              <span className="font-mono text-[11px] tracking-[0.3em] text-zinc-300">EST · TORONTO + GTA · CANADA</span>
            </div>
            <h1 className="font-display text-[11vw] sm:text-[8vw] lg:text-[6.5rem] leading-[0.9] tracking-tight uppercase">
              Custom Apparel<br/>
              <span className="text-[var(--tc-red)]">Printed Locally</span><br/>
              <span className="text-zinc-400 text-[9vw] sm:text-[6vw] lg:text-[4.5rem]">In Toronto & The GTA</span>
            </h1>
            <p className="font-body text-zinc-300 text-lg mt-8 max-w-2xl">
              Design your own T-shirts, hoodies, polos, hats and more online with live previews and instant checkout. Or shop our weekly drops of ready-made designs.
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/customize" data-testid="hero-start-designing" onClick={() => trackEvent("cta_hero_click", { cta: "start_designing", location: "hero" })}>
                <button className="btn-tc-primary flex items-center gap-3">
                  Start Designing <ArrowRight size={18} />
                </button>
              </Link>
              <Link to="/ready-made" data-testid="hero-readymade-link" onClick={() => trackEvent("cta_hero_click", { cta: "shop_readymade", location: "hero" })}>
                <button className="btn-tc-secondary">Shop Ready-Made Designs</button>
              </Link>
              <Link to="/dtf" data-testid="hero-dtf-link" onClick={() => trackEvent("cta_hero_click", { cta: "dtf_transfers", location: "hero" })}>
                <button className="btn-tc-secondary">DTF Transfers</button>
              </Link>
            </div>
            <TrustBadges className="mt-8" />
          </div>
          <div className="lg:col-span-4 hidden lg:flex flex-col gap-4">
            {[
              ["48H", "RUSH TURNAROUND"],
              ["NO MIN", "1 PIECE OR 10,000"],
              ["FREE PICKUP", "MISSISSAUGA STUDIO"],
            ].map(([k, v]) => (
              <div key={k} className="surface-card p-5">
                <div className="font-display text-4xl text-[var(--tc-red)]">{k}</div>
                <div className="font-mono text-[10px] tracking-widest text-zinc-400 mt-1">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      <ExplainerVideo />

      {/* FEATURED CUSTOM CATEGORIES */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 py-24" data-testid="custom-categories">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="font-mono text-[11px] tracking-widest text-zinc-500 mb-3">/ CUSTOMIZE_APPAREL</div>
            <h2 className="font-display text-6xl md:text-8xl uppercase leading-none">Make It Yours</h2>
          </div>
          <Link to="/customize" className="font-ui text-sm uppercase tracking-widest text-zinc-300 hover:text-white">All Categories →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {customProducts.slice(0, 8).map((p) => (
            <Link key={p.id} to={`/customize/${p.id}`} data-testid={`landing-custom-${p.id}`} className="surface-card group block">
              <div className="aspect-square bg-[#080808] overflow-hidden relative">
                <img src={p.image} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute top-3 left-3 chip bg-black/60 backdrop-blur">CUSTOM</div>
                <div className="absolute top-3 right-3 flex flex-col gap-1 items-end">
                  {(p.badges || []).includes("new-drop") && (
                    <div className="chip border-emerald-500 bg-black/70 backdrop-blur text-emerald-400" data-testid={`badge-newdrop-${p.id}`}>✨ NEW DROP</div>
                  )}
                  {(p.badges || []).includes("trending") && (
                    <div className="chip border-pink-500 bg-black/70 backdrop-blur text-pink-400" data-testid={`badge-trending-${p.id}`}>📈 TRENDING</div>
                  )}
                  {p.bestseller_rank && (
                    <div className="chip border-orange-500 bg-black/70 backdrop-blur text-orange-400" data-testid={`bestseller-badge-${p.id}`}>🔥 BEST SELLER</div>
                  )}
                </div>
              </div>
              <div className="p-4">
                <div className="font-ui font-bold uppercase tracking-wider text-xs">{p.name}</div>
                <div className="flex justify-between items-end mt-2">
                  <span className="font-mono text-[10px] tracking-widest text-zinc-500">FROM</span>
                  <span className="font-mono font-bold text-[var(--tc-red)]">${p.base_price?.toFixed(2)}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-y border-[#222] bg-[#050505]" data-testid="how-it-works">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-24">
          <div className="font-mono text-[11px] tracking-widest text-zinc-500 mb-3">/ HOW_IT_WORKS</div>
          <h2 className="font-display text-6xl md:text-8xl uppercase leading-none mb-12">Design In 4 Steps.</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { icon: Shirt, n: "01", t: "Choose Garment", d: "Pick from 8+ premium blank styles." },
              { icon: Upload, n: "02", t: "Upload Design", d: "PNG, JPG, SVG — or use our text + clipart tools." },
              { icon: Eye, n: "03", t: "Live Preview", d: "Position artwork front + back. See it before you buy." },
              { icon: ShoppingCart, n: "04", t: "Checkout", d: "Secure Square checkout. Ship or pick up." },
            ].map((s) => (
              <div key={s.n} className="border border-[#222] p-8 hover:border-[var(--tc-red)] transition-colors group">
                <div className="flex justify-between items-start mb-6">
                  <s.icon size={28} className="text-[var(--tc-red)]" />
                  <span className="font-display text-5xl text-zinc-800 group-hover:text-[#1a1a1a]">{s.n}</span>
                </div>
                <div className="font-ui font-bold uppercase tracking-wider text-sm">{s.t}</div>
                <p className="text-zinc-400 text-sm mt-2 font-body">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BEST SELLERS / TRENDING READY-MADE */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 py-24" data-testid="bestsellers">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="font-mono text-[11px] tracking-widest text-zinc-500 mb-3">/ BEST_SELLERS</div>
            <h2 className="font-display text-6xl md:text-8xl uppercase leading-none">Hot This Week</h2>
          </div>
          <Link to="/ready-made" className="font-ui text-sm uppercase tracking-widest text-zinc-300 hover:text-white">View All →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {readyMade.slice(0, 10).map((p) => <ReadyMadeCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* WEEKLY DESIGN DROPS */}
      <section className="border-y border-[#222] bg-gradient-to-b from-[#050505] to-black relative" data-testid="weekly-drops">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-10">
            <div className="lg:col-span-7">
              <div className="font-mono text-[11px] tracking-widest text-[var(--tc-blue)] mb-3">/ WEEKLY_DROPS · NEW</div>
              <h2 className="font-display text-6xl md:text-8xl uppercase leading-[0.85]">
                Fresh Drops<br/>Every Week.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="font-body text-zinc-300 text-lg">
                Anime, horror, gaming, sci-fi and seasonal designs — printed on premium tees. We add new collections every Thursday.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { c: "anime", l: "Anime" },
              { c: "horror", l: "Horror" },
              { c: "videogames", l: "Gaming" },
              { c: "sci-fi", l: "Sci-Fi" },
              { c: "movies", l: "Movies" },
              { c: "seasonal", l: "Seasonal" },
            ].map((c) => (
              <Link key={c.c} to={`/ready-made/${c.c}`} data-testid={`drop-${c.c}`} className="surface-card group block aspect-square relative overflow-hidden">
                <img src={readyMade.find((p) => (p.category_slugs || []).includes(c.c))?.image_front || RACK} alt={c.l} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 opacity-70" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <div className="font-display text-3xl uppercase">{c.l}</div>
                  <div className="font-mono text-[10px] tracking-widest text-[var(--tc-red)] mt-1">SHOP COLLECTION →</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* READY-MADE COLLECTIONS */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 py-24" data-testid="ready-made-collections">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="font-mono text-[11px] tracking-widest text-zinc-500 mb-3">/ COLLECTIONS</div>
            <h2 className="font-display text-6xl md:text-8xl uppercase leading-none">Shop By Universe</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {["comic-books", "magic-fantasy", "tv", "cartoons"].map((c) => (
            <Link key={c} to={`/ready-made/${c}`} className="surface-card group aspect-[4/5] relative overflow-hidden block">
              <img src={readyMade.find((p) => (p.category_slugs || []).includes(c))?.image_front || MODEL} alt={c} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 opacity-80" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
              <div className="absolute bottom-6 left-6">
                <div className="font-display text-4xl uppercase capitalize">{c.replace("-", " ")}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* BUSINESS / TEAM APPAREL */}
      <section className="border-y border-[#222] relative overflow-hidden" data-testid="business-section">
        <div className="absolute inset-0">
          <img src={SKYLINE} alt="Toronto skyline — local custom apparel printing" loading="lazy" decoding="async" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
        </div>
        <div className="relative max-w-[1500px] mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="font-mono text-[11px] tracking-widest text-[var(--tc-blue)] mb-3">/ BUSINESS · TEAMS · CREWS</div>
            <h2 className="font-display text-6xl md:text-8xl uppercase leading-[0.85]">
              Apparel For<br/>Your Whole Crew.
            </h2>
            <p className="font-body text-zinc-300 text-lg mt-6 max-w-xl">
              Restaurant staff, sports teams, construction crews, schools, corporate events. We handle teamwork from 10 pieces to 10,000.
            </p>
            <div className="flex gap-4 mt-8 flex-wrap">
              <Link to="/bulk-orders" onClick={() => trackEvent("cta_bulk_click", { cta: "bulk_orders", location: "business_section" })}><button className="btn-tc-primary flex items-center gap-3">Bulk Orders <ArrowRight size={18} /></button></Link>
              <Link to="/quote" onClick={() => trackEvent("cta_quote_click", { cta: "request_quote", location: "business_section" })}><button className="btn-tc-accent">Request a Quote</button></Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: Award, t: "Premium blanks" },
                { icon: Zap, t: "Rush available" },
                { icon: Sparkles, t: "Embroidery + DTF" },
                { icon: Truck, t: "Canada-wide ship" },
              ].map((f) => (
                <div key={f.t} className="surface-card p-5">
                  <f.icon size={20} className="text-[var(--tc-red)] mb-3" />
                  <div className="font-ui font-bold uppercase tracking-wider text-xs">{f.t}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BULK ORDERS CTA */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 py-24" data-testid="bulk-cta">
        <div className="grid lg:grid-cols-2 gap-10">
          {[
            { t: "Schools & Clubs", d: "Spirit wear, club tees, grad hoodies — printed in school colors with embroidered crest." },
            { t: "Restaurants & Cafés", d: "Branded staff polos, aprons, bandanas. Wash-tested for service-industry abuse." },
            { t: "Construction Crews", d: "Hi-vis tees, work hoodies, embroidered jackets — built for the job site." },
            { t: "Corporate Events", d: "Trade-show booth uniforms, conference giveaways, employee onboarding kits." },
          ].map((c) => (
            <div key={c.t} className="surface-card p-8 hover:border-[var(--tc-red)] transition-colors group">
              <div className="font-display text-4xl uppercase">{c.t}</div>
              <p className="text-zinc-400 mt-3 font-body">{c.d}</p>
              <Link to="/quote" className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-widest text-[var(--tc-red)] mt-4 group-hover:gap-3 transition-all">
                Get a quote →
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS — Real Google Business Profile reviews */}
      <ReviewsCarousel />

      {/* INSTAGRAM / SOCIAL FEED */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 py-24" data-testid="social-feed">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="font-mono text-[11px] tracking-widest text-zinc-500 mb-3">/ INSTAGRAM</div>
            <h2 className="font-display text-6xl md:text-8xl uppercase leading-none">In The Wild.</h2>
          </div>
          <a href="#" className="font-ui text-sm uppercase tracking-widest text-zinc-300 hover:text-white">@torontocustom →</a>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          {[MODEL, RACK, PRINT_PROCESS, SKYLINE, HERO_BG, MODEL].map((img, i) => (
            <a key={i} href="#" className="aspect-square overflow-hidden surface-card">
              <img src={img} alt="Custom printed apparel design sample" className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" />
            </a>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="border-y border-[#222] bg-[#050505]" data-testid="faq-preview">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-24">
          <div className="font-mono text-[11px] tracking-widest text-zinc-500 mb-3">FAQ</div>
          <h2 className="font-display text-6xl md:text-8xl uppercase leading-none mb-12">Got Questions?</h2>
          <div className="space-y-3">
            {FAQ_PREVIEW.map((f, i) => (
              <details key={i} className="surface-card group" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer p-6 flex justify-between items-center list-none">
                  <span className="font-ui font-bold uppercase tracking-wider text-sm">{f.q}</span>
                  <span className="text-[var(--tc-red)] font-display text-3xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <div className="px-6 pb-6 text-zinc-400 font-body">{f.a}</div>
              </details>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/faq"><button className="btn-tc-secondary">All FAQs →</button></Link>
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="relative overflow-hidden" data-testid="cta-banner">
        <div className="absolute inset-0">
          <img src={PRINT_PROCESS} alt="DTF printing process at our Mississauga shop" loading="lazy" decoding="async" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-br from-black via-black/70 to-[var(--tc-red)]/30" />
        </div>
        <div className="relative max-w-[1500px] mx-auto px-6 lg:px-10 py-32 lg:py-44 text-center">
          <div className="font-mono text-[11px] tracking-widest text-[var(--tc-red)] mb-4">/ READY?</div>
          <h2 className="font-display text-6xl md:text-9xl uppercase leading-[0.85]">
            Print Your Idea<br/>This Week.
          </h2>
          <p className="text-zinc-300 mt-6 max-w-lg mx-auto font-body text-lg">
            From sketch to shipped in under 7 days. Free local pickup in Mississauga.
          </p>
          <div className="flex justify-center gap-4 mt-8 flex-wrap">
            <Link to="/customize" onClick={() => trackEvent("cta_banner_click", { cta: "start_designing", location: "footer_cta" })}><button className="btn-tc-primary">Start Designing</button></Link>
            <Link to="/ready-made" onClick={() => trackEvent("cta_banner_click", { cta: "shop_drops", location: "footer_cta" })}><button className="btn-tc-secondary">Shop Drops</button></Link>
            <Link to="/dtf" onClick={() => trackEvent("cta_banner_click", { cta: "dtf_transfers", location: "footer_cta" })}><button className="btn-tc-secondary">DTF Transfers</button></Link>
          </div>
          <div className="flex items-center justify-center gap-2 mt-10 text-zinc-500 text-xs font-mono">
            <MapPin size={12} /> LOCAL PICKUP · 2404 HAINES RD SUITE 209 · MISSISSAUGA
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
